if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (window.Shopify?.postLinksRetry && typeof this.productInfo?.initShareLinks === 'function') this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;
      if (!this.elements.thumbnails) return;

      if (!this.elements.viewer) return;
      this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
      this.progress = this.querySelector('.gallery-progress span');
      this.mediaList = this.elements.viewer.querySelector('.product__media-list');
      this.mediaList?.addEventListener('scroll', () => this.updateProgress(), { passive: true });
      this.updateProgress();
      this.mediaResizeObserver = new ResizeObserver(() => this.fitActiveSlide());
      this.elements.viewer.querySelectorAll('.product-media-container').forEach(item => this.mediaResizeObserver.observe(item));
      this.fitActiveSlide();
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      this.elements.thumbnails.querySelectorAll('button[name="previous"], button[name="next"]').forEach(button => {
        // These arrows change the main image, even when all thumbnails fit.
        button.disabled = false;
        button.addEventListener('click', event => {
          event.preventDefault();
          event.stopImmediatePropagation();
          const items = [...this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')]
            .filter(item => !item.classList.contains('hidden'));
          if (!items.length) return;
          const index = items.findIndex(item => item.classList.contains('is-active'));
          const direction = button.name === 'next' ? 1 : -1;
          this.setActiveMedia(items[(index + direction + items.length) % items.length].dataset.mediaId, false);
        }, true);
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    fitActiveSlide() {
      if (!this.mediaList) return;
      if (this.mql.matches) { this.mediaList.style.height = ''; return; }
      const active = this.elements.viewer.querySelector('.product__media-item.is-active .product-media-container');
      if (active && active.getBoundingClientRect().height > 0) this.mediaList.style.height = `${active.getBoundingClientRect().height}px`;
    }

    updateProgress() {
      if (!this.progress || !this.mediaList) return;
      const slides = [...this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')].filter(item => !item.classList.contains('hidden'));
      const count = Math.max(slides.length, 1);
      const maxScroll = this.mediaList.scrollWidth - this.mediaList.clientWidth;
      const index = Math.max(0, slides.findIndex(item => item.classList.contains('is-active')));
      const fraction = maxScroll > 0 ? Math.max(0, Math.min(1, this.mediaList.scrollLeft / maxScroll)) : index / Math.max(count - 1, 1);
      this.progress.style.width = `${100 / count}%`;
      this.progress.style.transform = `translateX(${fraction * (count - 1) * 100}%)`;
    }

    onSlideChanged(event) {
      const currentMedia = event.detail?.currentElement;
      if (!currentMedia || !this.elements.viewer.contains(currentMedia)) return;
      // Swipes and slider controls must update the same state used by the arrows.
      this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach(item => {
        item.classList.toggle('is-active', item === currentMedia);
      });
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${currentMedia.dataset.mediaId}"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        })
      }

      const activeMedia = this.elements.viewer.querySelector(`.product__media-item[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;
      this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          const list = activeMedia.parentElement;
          const left = list.scrollLeft + activeMedia.getBoundingClientRect().left - list.getBoundingClientRect().left;
          // Avoid intermediate smooth-scroll events restoring the previous slide.
          list.scrollTo({ left, behavior: 'instant' });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      this.updateProgress();
      this.fitActiveSlide();
      const list = this.elements.thumbnails.querySelector('.thumbnail-list');
      if (!list) return;
      const bounds = list.getBoundingClientRect();
      const itemBounds = thumbnail.getBoundingClientRect();
      if (itemBounds.left >= bounds.left && itemBounds.right <= bounds.right && itemBounds.top >= bounds.top && itemBounds.bottom <= bounds.bottom) return;
      list.scrollTo({ left: list.scrollLeft + itemBounds.left - bounds.left, top: list.scrollTop + itemBounds.top - bounds.top });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image || !this.elements.liveRegion || !window.accessibilityStrings?.imageAvailable) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (typeof deferredMedia?.loadContent === 'function') deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
